/** @jsx h */
import { Fragment, h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { 
  CheckIcon,
  CloseIcon,
  PencilIcon,
  MinusIcon,
  PlusIcon,
  StockIcon,
  OutOfStockIcon,
  ArrowUpIcon,
  ArrowBackIcon,
  SaveIcon,
} from './yourListsIcons';
import fetchListProducts, {
  fetchListPrices,
  fetchRecommendation,
  isOutOfStock,
} from '../objects/your-lists/fetchListProducts';
import { add as addToCart } from '../objects/cart/ajax_cart';
import { updateCart, removeCartLines } from '../objects/your-lists/updateCart';
import { loadEdits, saveEdits, clearEdits } from '../objects/your-lists/listEdits';
import { groupListRows } from '../objects/your-lists/groupListRows';
import CartResultModal from './CartResultModal';
import CartExtrasModal from './CartExtrasModal';

const STOCK_KEY = { in: 'in_stock', low: 'low_stock', out: 'out_of_stock' };

// "Don't ask me again" for the remove confirmation. Stored in sessionStorage
// so it resets when the tab/browser closes.
const SKIP_REMOVE_KEY = 'your-lists:skip-remove-confirm';

function loadSkipRemove() {
  try {
    return sessionStorage.getItem(SKIP_REMOVE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function saveSkipRemove(value) {
  try {
    if (value) sessionStorage.setItem(SKIP_REMOVE_KEY, '1');
  } catch (e) {
    // storage unavailable — non-fatal
  }
}

/**
 * Quantity stepper: editable input + −/+ buttons, clamped to 0..max.
 * Controlled — `value` comes from the parent (persisted); `onChange(next)`
 * reports the clamped new quantity. `max` is available stock (null = no cap).
 */
function QtyStepper({ value = 0, max = null, onChange, i18n = {} }) {
  const t = (key) => i18n[key] || key;
  const hasMax = typeof max === 'number';
  const clamp = (n) => {
    const v = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
    return hasMax ? Math.min(v, max) : v;
  };
  const qty = clamp(value);
  const outOfStock = hasMax && max <= 0;
  const set = (n) => onChange && onChange(clamp(n));

  const handleInput = (e) => {
    const clamped = clamp(parseInt(e.currentTarget.value, 10));
    // Force the clamped value back into the DOM: when `clamped` equals the
    // current value (e.g. stock is 0, or typing above max), the parent state
    // doesn't change, so the typed text would otherwise stick.
    e.currentTarget.value = String(clamped);
    set(clamped);
  };

  return (
    <div className={`page--qty-wrap${qty > 0 ? ' is-active' : ''}`}>
      <button
        type="button"
        className="page--qty-btn"
        aria-label={t('decrease_quantity')}
        disabled={qty <= 0}
        onClick={() => set(qty - 1)}
      >
        <MinusIcon />
      </button>
      <input
        type="number"
        className="page--qty-value"
        min="0"
        max={hasMax ? max : undefined}
        value={qty}
        disabled={outOfStock}
        aria-label={t('quantity')}
        onInput={handleInput}
      />
      <button
        type="button"
        className="page--qty-btn"
        aria-label={t('increase_quantity')}
        disabled={hasMax && qty >= max}
        onClick={() => set(qty + 1)}
      >
        <PlusIcon />
      </button>
    </div>
  );
}

/**
 * "You may like" recommendation card for an out-of-stock item.
 * Add to Cart reuses the theme's ajax cart (B2B company stamping + drawer /
 * notification refresh), mirroring cardAddtoCart.js.
 */
function YmlCard({ rec, i18n = {} }) {
  const t = (key) => i18n[key] || key;
  const [adding, setAdding] = useState(false);

  const handleAdd = () => {
    if (!rec.variantId || adding) return;

    const cartEl =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');
    const sections = cartEl?.getSectionsToRender
      ? cartEl.getSectionsToRender().map((s) => s.id).toString()
      : '';

    setAdding(true);
    addToCart(rec.variantId, 1, sections, (response) => {
      try {
        const data = JSON.parse(response);
        if (cartEl?.classList.contains('is-empty')) {
          cartEl.classList.remove('is-empty');
        }
        cartEl?.renderContents?.(data);
        // COB2B-811: the cart-notification-popup card is for product cards and the PDP only.
      } catch (e) {
        // ignore parse/render errors
      } finally {
        setAdding(false);
      }
    });
  };

  return (
    <div className="page--yml">
      <a className="page--yml-media" href={rec.url}>
        {rec.image ? (
          <img src={rec.image} alt={rec.title} />
        ) : (
          <span className="page--yml-media--placeholder" aria-hidden="true" />
        )}
      </a>
      <div className="page--yml-content">
        <div className="page--yml-content-title">
          {t('you_may_like')}
        </div>
        <a className="page--yml-title" href={rec.url}>
          {rec.title}
        </a>
      </div>
      <div className="page--yml-add-wrap">
        <button
          type="button"
          className="page--yml-add"
          disabled={adding}
          onClick={handleAdd}
        >
          {adding ? t('adding') : t('add_to_cart_short')}
        </button>
      </div>
    </div>
  );
}

/** Floating "back to top" button — appears once the page is scrolled. */
function ScrollToTop({ i18n = {} }) {
  const t = (key) => i18n[key] || key;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <button
      type="button"
      className={`page--scroll-top${visible ? ' is-visible' : ''}`}
      aria-label={t('back_to_top')}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <ArrowUpIcon />
    </button>
  );
}

function YourListDetail({
  list,
  i18n = {},
  accessToken = '',
  badgeIcon = '',
  noImage = '',
  tagBadges = [],
  onBack,
  onRename,
  onListChange,
}) {
  const t = (key) => i18n[key] || key;
  const items = Array.isArray(list.items) ? list.items : [];

  // Inline title editing (pencil → input + confirm).
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(list.name);
  const titleInputRef = useRef(null);

  const startEditTitle = () => {
    setTitleDraft(list.name);
    setEditingTitle(true);
  };

  const confirmTitle = () => {
    const next = titleDraft.trim();
    // Only save when the title actually changed (onRename handles the save).
    if (next && next !== list.name) onRename && onRename(next);
    setEditingTitle(false);
  };

  const handleTitleKey = (e) => {
    if (e.key === 'Enter') confirmTitle();
    else if (e.key === 'Escape') setEditingTitle(false);
  };

  // Focus the input when entering edit mode.
  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  // Product display data resolved from variant ids: { [variantId]: {...} }.
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(items.length > 0);
  const [error, setError] = useState(null);
  // Selected variant ids (controlled). Out-of-stock items are never selectable.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Local edits (persisted to sessionStorage): quantity per id + removed ids.
  const [quantities, setQuantities] = useState({});
  const [removedIds, setRemovedIds] = useState(() => new Set());
  // True when this list's state was restored from sessionStorage — so the
  // default (products-based) selection seeding doesn't clobber it.
  const restoredRef = useRef(false);
  // Company-aware price HTML per variant id (from the Section Rendering API).
  const [prices, setPrices] = useState({});
  // The `products` map `prices` was fetched for. The rows wait until they
  // match: the price section also carries each row's group heading (Level 2
  // category, COB2B-785), so showing rows earlier would regroup them on arrival.
  const [pricesFor, setPricesFor] = useState(null);
  // In-stock recommendation per out-of-stock variant id (Recommendations API).
  const [recs, setRecs] = useState({});
  // True while "Save and update cart" is running.
  const [savingCart, setSavingCart] = useState(false);
  // True while the standalone "Save List" button is running.
  const [savingList, setSavingList] = useState(false);
  // Set when a list save (from either save button) fails.
  const [saveError, setSaveError] = useState(null);
  // Cart update result report ({ lines }) shown in a modal, or null.
  const [cartResult, setCartResult] = useState(null);
  // Cart products not in this list ([{ id, title, quantity, image }]), or null:
  // shown in a modal asking the user to keep or remove them.
  const [cartExtras, setCartExtras] = useState(null);
  // Remove confirmation: id of the item pending removal (null = closed).
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  // Restore local edits (or seed defaults) whenever the viewed list changes.
  useEffect(() => {
    const saved = loadEdits(list.id);
    if (saved) {
      restoredRef.current = true;
      setQuantities(saved.quantities || {});
      setSelectedIds(new Set(saved.selected || []));
      setRemovedIds(new Set(saved.removed || []));
    } else {
      restoredRef.current = false;
      const q = {};
      items.forEach((it) => {
        q[it.id] = it.quantity ?? 0;
      });
      setQuantities(q);
      setSelectedIds(new Set()); // seeded by the products effect below
      setRemovedIds(new Set());
    }
  }, [list.id]);

  useEffect(() => {
    if (items.length === 0) {
      setProducts({});
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchListProducts(items.map((it) => it.id), accessToken)
      .then((map) => {
        if (!cancelled) setProducts(map || {});
      })
      .catch(() => {
        if (!cancelled) setError(t('load_error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Re-fetch when the viewed list changes.
  }, [list.id, accessToken]);

  // Seed selection from saved `selected` once products resolve — but never
  // pre-select an out-of-stock item. Skipped when edits were restored from
  // sessionStorage (that selection wins).
  useEffect(() => {
    if (restoredRef.current) return;
    const seed = new Set();
    items.forEach((it) => {
      const product = products[it.id];
      if (product && it.selected && !isOutOfStock(product)) seed.add(it.id);
    });
    setSelectedIds(seed);
  }, [products]);

  // Fetch company-aware price HTML once products (and their handles) resolve.
  useEffect(() => {
    const list = Object.values(products);
    if (list.length === 0) {
      setPrices({});
      setPricesFor(products);
      return undefined;
    }
    let cancelled = false;
    fetchListPrices(list).then((map) => {
      if (cancelled) return;
      setPrices(map);
      setPricesFor(products);
    });
    return () => {
      cancelled = true;
    };
  }, [products]);

  // For out-of-stock items, fetch one in-stock recommendation ("You may like").
  useEffect(() => {
    const oosProducts = Object.values(products).filter(
      (p) => isOutOfStock(p) && p.productId,
    );
    if (oosProducts.length === 0) {
      setRecs({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      oosProducts.map(async (p) => [p.id, await fetchRecommendation(p.productId)]),
    ).then((entries) => {
      if (!cancelled) setRecs(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [products]);

  const othersLabel = t('others');

  // The row's deal badge: the company's flag from the price section, else a
  // plain DEAL when the product is on sale. '' = no badge.
  const dealFlag = (item) =>
    prices[item.id]?.flag || (item.onSale ? 'DEAL' : '');

  // Merge saved per-item state with fetched product data. Items whose variant
  // couldn't be resolved (deleted / not returned) become a minimal "missing"
  // row grouped under "Others": name only (from the saved item) + remove.
  const rows = items
    .filter((it) => !removedIds.has(it.id))
    .map((it) => {
      const product = products[it.id];
      if (!product) {
        return {
          id: it.id,
          title: it.title || '',
          category: othersLabel,
          image: noImage,
          missing: true,
        };
      }
      return {
        ...product,
        // Level 2 category from the price section (same as the mini cart's
        // "View Ordered Categories"); the product's own category otherwise.
        category: prices[it.id]?.category || product.category,
        // Level 1 parent and its menu position, to sort the groups by.
        categoryParent: prices[it.id]?.category
          ? prices[it.id].categoryParent
          : '',
        categoryParentIndex: prices[it.id]?.category
          ? prices[it.id].categoryParentIndex
          : null,
        quantity: quantities[it.id] ?? it.quantity ?? 0,
      };
    });

  // Group by Level 2 category: groups in Level 1 menu order then A–Z, rows
  // A–Z by title, "Others" last (COB2B-785).
  const groups = groupListRows(rows, othersLabel);

  // Select-all only concerns selectable rows: resolved and in stock.
  const selectableIds = rows
    .filter((r) => !r.missing && !isOutOfStock(r))
    .map((r) => r.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  // Rebuild the list's stored items from the current edits: drop removed items,
  // apply per-item quantity and selected state. This is the value that gets
  // saved back to the list's metafield slot.
  const buildItems = (q, sel, rem) =>
    items
      .filter((it) => !rem.has(it.id))
      .map((it) => ({
        ...it,
        quantity: q[it.id] ?? it.quantity ?? 0,
        selected: sel.has(it.id),
      }));

  // Whether the current edits differ from `list.items` (the last-saved
  // baseline) — i.e. there's something a save button would actually send.
  // Edits only live locally (persist(), above) until saveListNow runs, so
  // this stays true until the user explicitly saves.
  const hasUnsavedChanges = items.some((it) => {
    if (removedIds.has(it.id)) return true;
    const currentQty = quantities[it.id] ?? it.quantity ?? 0;
    const currentSelected = selectedIds.has(it.id);
    return currentQty !== (it.quantity ?? 0) || currentSelected !== !!it.selected;
  });

  // Persist the current edits (with the given overrides) to sessionStorage
  // ONLY — this is a local draft, not a save. Nothing reaches Shopify until
  // the user explicitly clicks "Save List" or "Save and update cart"
  // (saveListNow, below), so quantity/select/remove edits are safe to try
  // out without committing them.
  const persist = ({ q = quantities, sel = selectedIds, rem = removedIds }) => {
    setSaveError(null);
    saveEdits(list.id, {
      quantities: q,
      selected: [...sel],
      removed: [...rem],
    });
  };

  const setQuantity = (id, q) => {
    const next = { ...quantities, [id]: q };
    setQuantities(next);
    persist({ q: next });
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    persist({ sel: next });
  };

  const toggleSelectAll = () => {
    const next = allSelected ? new Set() : new Set(selectableIds);
    setSelectedIds(next);
    persist({ sel: next });
  };

  const removeItem = (id) => {
    const nextRemoved = new Set(removedIds).add(id);
    const nextSelected = new Set(selectedIds);
    nextSelected.delete(id);
    setRemovedIds(nextRemoved);
    setSelectedIds(nextSelected);
    persist({ sel: nextSelected, rem: nextRemoved });
  };

  // Ask for confirmation before removing — unless the user opted out.
  const requestRemove = (id) => {
    if (loadSkipRemove()) {
      removeItem(id);
      return;
    }
    setDontAskAgain(false);
    setConfirmRemoveId(id);
  };

  const confirmRemove = () => {
    if (dontAskAgain) saveSkipRemove(true);
    removeItem(confirmRemoveId);
    setConfirmRemoveId(null);
  };

  const cancelRemove = () => setConfirmRemoveId(null);

  // Close the confirmation on Esc + lock background scroll while it's open.
  useEffect(() => {
    if (confirmRemoveId == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setConfirmRemoveId(null);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [confirmRemoveId]);

  // Persist quantities/selected/removed to the list's metafield slot via
  // onListChange (real API — see touchList in YourLists.jsx). On success,
  // drop the sessionStorage draft: the server copy is now the source of
  // truth. Shared by the standalone "Save List" button and step 1 of
  // "Save and update cart". Returns whether the save succeeded.
  const saveListNow = async () => {
    setSaveError(null);
    const builtItems = buildItems(quantities, selectedIds, removedIds);
    const saved = onListChange ? await onListChange(builtItems) : true;
    if (saved) {
      clearEdits(list.id);
    } else {
      setSaveError(t('save_failed'));
    }
    return saved;
  };

  const handleSaveList = async () => {
    if (savingList || savingCart || !hasUnsavedChanges) return;
    setSavingList(true);
    try {
      await saveListNow();
    } finally {
      setSavingList(false);
    }
  };

  // "Save and update cart":
  // 1. Save the list (above) — don't update the cart from state that didn't
  //    actually save.
  // 2. Update cart (below): for each selected item, set the cart quantity to
  //    the list quantity — update if it's already in the cart, add if not.
  const handleSaveAndUpdateCart = async () => {
    if (selectedIds.size === 0 || savingCart) return;

    setSavingCart(true);
    try {
      const saved = await saveListNow();
      if (!saved) return;

      const selected = [...selectedIds].map((id) => ({
        id,
        quantity: quantities[id] ?? 0,
        title: products[id]?.title,
      }));
      // Full list membership (all items, selected or not) so cart products that
      // belong to the list are never flagged as extras.
      const listItemIds = items.map((it) => it.id);
      const report = await updateCart(selected, listItemIds);
      // Show the keep/remove prompt when the cart has products outside the list.
      if (report.extras && report.extras.length) {
        setCartExtras(report.extras);
      }
      // NOTE: the "added / updated" result report is disabled for now — keep the
      // code so it can be re-enabled later.
      // else if (report.lines.length) {
      //   setCartResult(report);
      // }
    } catch (e) {
      // TODO: surface a cart-update error to the user
    } finally {
      setSavingCart(false);
    }
  };

  // "Remove" on the extras prompt: drop the non-list products from the cart.
  const handleRemoveExtras = async () => {
    if (!cartExtras || cartExtras.length === 0) return;
    await removeCartLines(cartExtras.map((e) => e.id));
    setCartExtras(null);
  };

  return (
    <div className="page--list-detail">
      <button type="button" className="page--ld-back" onClick={onBack}>
        <span className="page--ld-back-icon" aria-hidden="true">
          <ArrowBackIcon />
        </span>
        {t('back_to_lists')}
      </button>

      <div className="page--ld-header">
        <div
          className={`page--ld-title-wrap${editingTitle ? ' is-editing' : ''}`}
        >
          {editingTitle ? (
            <Fragment>
              <input
                ref={titleInputRef}
                type="text"
                className="page--ld-title-input"
                value={titleDraft}
                aria-label={t('edit_list')}
                onInput={(e) => setTitleDraft(e.currentTarget.value)}
                onKeyDown={handleTitleKey}
              />
              <button
                type="button"
                className="page--ld-edit"
                aria-label={t('save')}
                onClick={confirmTitle}
              >
                <SaveIcon />
              </button>
            </Fragment>
          ) : (
            <Fragment>
              <h1 className="page--ld-title">{list.name}</h1>
              <button
                type="button"
                className="page--ld-edit"
                aria-label={t('edit_list')}
                onClick={startEditTitle}
              >
                <PencilIcon />
              </button>
            </Fragment>
          )}
        </div>

        <div className="page--ld-actions">
          <button
            type="button"
            className={`page--ld-select-all${allSelected ? ' is-unselect' : ''}`}
            disabled={editingTitle || selectableIds.length === 0}
            onClick={toggleSelectAll}
          >
            {allSelected ? t('unselect_all') : t('select_all')}
          </button>
          <button
            type="button"
            className={`page--ld-save-list${savingList ? ' is-loading' : ''}`}
            disabled={editingTitle || savingList || savingCart || !hasUnsavedChanges}
            onClick={handleSaveList}
          >
            {/* Label stays in the layout (hidden while saving) so the button
                keeps its width; the dots overlay it. */}
            <span className="page--ld-save-list-label">{t('save_list')}</span>
            {savingList && (
              <span className="page--ld-dots" role="status" aria-label={t('saving_list')}>
                <span />
                <span />
                <span />
              </span>
            )}
          </button>
          <button
            type="button"
            className={`page--ld-add-cart${savingCart ? ' is-loading' : ''}`}
            disabled={editingTitle || savingCart || selectedIds.size === 0}
            onClick={handleSaveAndUpdateCart}
          >
            {/* Label stays in the layout (hidden while saving) so the button
                keeps its width; the dots overlay it. */}
            <span className="page--ld-add-cart-label">{t('add_to_cart')}</span>
            {savingCart && (
              <span className="page--ld-dots" role="status" aria-label={t('adding')}>
                <span />
                <span />
                <span />
              </span>
            )}
          </button>
        </div>
      </div>

      {saveError && <div className="page--ld-save-error">{saveError}</div>}

      {(() => {
        if (loading || pricesFor !== products) {
          return <div className="page--ld-state">{t('loading')}</div>;
        }
        if (error) {
          return <div className="page--ld-state page--ld-state--error">{error}</div>;
        }
        if (items.length === 0 || rows.length === 0) {
          return (
            <div className="page--no-lists">
              <div className="page--no-lists-description">{t('empty_list')}</div>
            </div>
          );
        }
        return groups.map(({ key, category, rows: products_ }) => (
          <section className="page--ld-group" key={key}>
            <div className="page--ld-group-title">{category}</div>

            <ul className="page--ld-products">
              {products_.map((item) => {
                const oos = !item.missing && isOutOfStock(item);

                const mediaCell = (
                  <div className="page--product-media">
                    {/* Certification badge — only when the product has one
                        (custom.badges_to_display_plp), mirroring card-product.liquid */}
                    {!item.missing && item.badge && (
                      <span className="page--product-badge">
                        {badgeIcon && (
                          <img className="badge-top" src={badgeIcon} alt="" aria-hidden="true" />
                        )}
                        {item.badge}
                        {badgeIcon && (
                          <img className="badge-bottom" src={badgeIcon} alt="" aria-hidden="true" />
                        )}
                      </span>
                    )}
                    {item.image ? (
                      <img className="page--product-img" src={item.image} alt={item.title} />
                    ) : (
                      <span
                        className="page--product-img page--product-img--placeholder"
                        aria-hidden="true"
                      />
                    )}

                    {/* Deal + tag badges (NEW etc.), like product-tag-labels.liquid.
                        COB2B-785: the deal is the company's DEAL / SUPERDEAL flag
                        PLP cards badge, or the product being on sale. */}
                    {!item.missing &&
                      (dealFlag(item) ||
                        (item.tags || []).some((tag) => tagBadges.some((b) => b.tag === tag))) && (
                        <div className="page--product-deals">
                          {dealFlag(item) && (
                            <span className="page--product-deal page--product-deal--sale">
                              {dealFlag(item) === 'SUPERDEAL'
                                ? 'Super Deal'
                                : t('deal')}
                            </span>
                          )}
                          {tagBadges
                            .filter((b) => (item.tags || []).includes(b.tag))
                            .map((b) => (
                              <span
                                key={b.tag}
                                className="page--product-deal"
                                style={{ backgroundColor: b.colour }}
                              >
                                {b.text}
                              </span>
                            ))}
                        </div>
                      )}
                  </div>
                );

                const infoCell = (
                  <div className="page--product-info">
                    {!item.missing && (item.sku || item.productNumber) && (
                      <span className="page--product-sku">
                        {item.sku || item.productNumber}
                      </span>
                    )}
                    {!item.missing && (
                      <a href={`/collections/vendors?q=${item.brand}`} className="page--product-brand">{item.brand}</a>
                    )}
                    {item.url ? (
                      <a className="page--product-title full-unstyled-link" href={item.url}>
                        {item.title}
                      </a>
                    ) : (
                      <span className="page--product-title">{item.title}</span>
                    )}
                    {!item.missing && (
                      <span
                        className={`page--product-stock page--product-stock--${item.stock}`}
                      >
                        {item.stock == 'out' && <OutOfStockIcon />}
                        {item.stock == 'in' && <StockIcon />}
                        {item.stock == 'low' && <StockIcon />}
                        {t(STOCK_KEY[item.stock] || 'in_stock')}
                      </span>
                    )}
                    {item.url && prices[item.id]?.hasVolume && (
                      <a className="page--product-volume mobile-only" href={item.url}>
                        {t('volume_pricing')}
                      </a>
                    )}
                  </div>
                );

                const priceCell = (
                  <div className="page--product-price">
                    {/* Company-aware price HTML from the Section Rendering API
                        (reuses price.liquid). Falls back to the base priceLabel. */}
                    {prices[item.id]?.html ? (
                      <div
                        className="page--product-price-block"
                        dangerouslySetInnerHTML={{ __html: prices[item.id].html }}
                      />
                    ) : (
                      item.priceLabel && (
                        <span className="page--product-price-main">{item.priceLabel}</span>
                      )
                    )}
                    {/* Only when the company has a volume deal for it (as on PLP/PDP). */}
                    {item.url && prices[item.id]?.hasVolume && (
                      <a className="page--product-volume" href={item.url}>
                        {t('volume_pricing')}
                      </a>
                    )}
                  </div>
                );

                const qtyCell = (
                  <div className="page--product-qty">
                    <QtyStepper
                      value={item.quantity}
                      max={item.maxQty}
                      onChange={(q) => setQuantity(item.id, q)}
                      i18n={i18n}
                    />
                  </div>
                );

                // Out of stock: one cell replacing price/qty — ETA on top,
                // "You may like" below. The ETA is the product's
                // custom.inventory_arrival_message, the same metafield
                // card-product.liquid shows; an em dash while it's unset.
                const oosCell = (
                  <div className="page--product-oos">
                    <div className="page--product-eta">
                      <span className="page--product-eta-label">{t('eta')}</span>
                      <span className="page--product-eta-value">
                        {item.eta || '—'}
                      </span>
                    </div>
                    <div className="page--product-suggestions">
                      {recs[item.id] && (
                        <YmlCard rec={recs[item.id]} i18n={i18n} />
                      )}
                    </div>
                  </div>
                );

                const checkboxCell = !item.missing && (
                  <div className="page--product-check-wrap">
                    <label className="page--product-check">
                      <input
                        type="checkbox"
                        className="page--product-check-input"
                        checked={selectedIds.has(item.id)}
                        disabled={isOutOfStock(item)}
                        aria-label={t('select_item')}
                        onChange={() => toggleOne(item.id)}
                      />
                      <span className="page--product-check-box">
                        <CheckIcon />
                      </span>
                    </label>
                  </div>
                );

                const removeCell = (
                  <div className="page--product-remove-wrap">
                    <button
                      type="button"
                      className="page--product-remove"
                      aria-label={t('remove_item')}
                      onClick={() => requestRemove(item.id)}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                );

                const className = `page--product-row${
                  item.missing ? ' page--product-row--missing' : ''
                }${dealFlag(item) ? ' page--product-row--sale' : ''}${
                  oos ? ' page--product-row--oos' : ''
                }`;

                return (
                  <li className={className} key={item.id}>
                    {mediaCell}
                    {infoCell}
                    {!item.missing && (oos ? oosCell : (
                      <Fragment>
                        {priceCell}
                        {qtyCell}
                      </Fragment>
                    ))}
                    {checkboxCell}
                    {removeCell}
                  </li>
                );
              })}
            </ul>
          </section>
        ));
      })()}

      {confirmRemoveId != null && (
        <div
          className="page--ld-confirm-overlay"
          role="presentation"
          onClick={cancelRemove}
        >
          <div
            className="page--ld-confirm"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="page--ld-confirm-close"
              aria-label={t('cancel')}
              onClick={cancelRemove}
            >
              <CloseIcon />
            </button>

            <div className="page--ld-confirm-title">
              {t('remove_confirm_title')}
            </div>

            <label className="page--ld-confirm-dontask">
              <input
                type="checkbox"
                className="page--ld-confirm-dontask-input"
                checked={dontAskAgain}
                onChange={(e) => setDontAskAgain(e.currentTarget.checked)}
              />
              <span className="page--ld-confirm-dontask-box">
                <CheckIcon />
              </span>
              {t('dont_ask_again')}
            </label>

            <div className="page--ld-confirm-actions">
              <button
                type="button"
                className="page--ld-confirm-delete"
                onClick={confirmRemove}
              >
                {t('delete')}
              </button>
              <button
                type="button"
                className="page--ld-confirm-cancel"
                onClick={cancelRemove}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Added / updated result report — disabled for now, kept for later.
      <CartResultModal
        report={cartResult}
        i18n={i18n}
        onClose={() => setCartResult(null)}
      />
      */}

      <CartExtrasModal
        extras={cartExtras}
        i18n={i18n}
        onKeep={() => setCartExtras(null)}
        onRemove={handleRemoveExtras}
      />

      <ScrollToTop i18n={i18n} />
    </div>
  );
}

export default YourListDetail;
